'use client';

import { useState, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { useAuth } from '@/components/auth-provider';
import { usePushNotifications } from '@/components/sw-registration';
import { BottomSheet } from '@/components/bottom-sheet';
import { useT } from '@/components/i18n-provider';
import { useAnalytics } from '@/hooks/use-analytics';

const DISMISSED_KEY = 'push-prompt-dismissed';
const SHOW_DELAY_MS = 3 * 60 * 1000;

export function PushPrompt() {
  const { user } = useAuth();
  const { requestPermission, subscribeToPush } = usePushNotifications();
  const [visible, setVisible] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const t = useT();
  const { trackPromptResponse } = useAnalytics();

  useEffect(() => {
    if (!user) return;
    if (typeof window === 'undefined' || !('Notification' in window) || !('serviceWorker' in navigator)) return;
    // No service worker in development (by design — see sw-registration), so
    // push can never complete there: don't ask for a permission we can't use.
    if (process.env.NODE_ENV !== 'production') return;
    if (Notification.permission !== 'default') return;

    const dismissed = localStorage.getItem(DISMISSED_KEY);
    if (dismissed) {
      const ts = parseInt(dismissed, 10);
      if (Date.now() - ts < 7 * 24 * 60 * 60 * 1000) return;
    }

    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [user]);

  const handleEnable = async () => {
    trackPromptResponse('push', 'accept');
    setEnabling(true);
    try {
      const granted = await requestPermission();
      if (granted) {
        const sub = await subscribeToPush();
        if (sub) {
          await fetch('/api/users/me/settings', {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ pushNotifications: true }),
          });
        } else {
          console.warn('[push-prompt] subscribeToPush returned null — subscription not registered');
        }
      }
    } catch (err) {
      console.error('[push-prompt] Failed to enable push:', err);
    } finally {
      // Always release the button and close — a hung or failed subscribe must
      // never strand the sheet on "Enabling…".
      setEnabling(false);
      setVisible(false);
    }
  };

  const handleDismiss = () => {
    trackPromptResponse('push', 'dismiss');
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    setVisible(false);
  };

  return (
    <BottomSheet open={visible} onClose={handleDismiss} desktopStackSlot="default">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <Bell className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">{t('push.stayInLoop')}</p>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            {t('push.description')}
          </p>
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleEnable}
              disabled={enabling}
              className="btn-primary text-xs px-4 py-1.5 rounded-lg"
            >
              {enabling ? t('push.enabling') : t('push.enable')}
            </button>
            <button
              onClick={handleDismiss}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors px-3 py-1.5"
            >
              {t('push.notNow')}
            </button>
          </div>
        </div>
      </div>
    </BottomSheet>
  );
}
